import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  Button,
  CircularProgress,
  Fab,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { getPrivateSettings } from "../../api/app";
import { login } from "../../api/auth";
import { useTranslationHook } from "../../hooks";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { setPrivateSettings } from "../../store/App";
import { setSessionLogin } from "../../store/Session";
import { useAccountPermissions } from "../../hooks/Permissions/useAccountPermissions";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const LoginContainer = styled(Stack)(({ theme }) => ({
  height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
  minHeight: "100%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
    }),
  },
}));

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslationHook();
  const handleError = useErrorHandler();
  const { isEntityUser } = useAccountPermissions();
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [isRunning, setIsRunning] = React.useState(false);

  const validateInputs = () => {
    const email = document.getElementById("email") as HTMLInputElement;
    const password = document.getElementById("password") as HTMLInputElement;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      enqueueSnackbar(t("generic.login.errors.invalid-email"), {
        variant: "error",
      });
      return false;
    }

    if (!password.value || password.value.length < 3) {
      enqueueSnackbar(t("generic.login.errors.invalid-password"), {
        variant: "error",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsRunning(true);

    const data = new FormData(event.currentTarget);
    const email = data.get("email");
    const password = data.get("password");

    if (typeof email !== "string" || typeof password !== "string") {
      throw new TypeError("Invalid form data");
    }

    try {
      const response = await login({ email, password });
      dispatch(setSessionLogin(response));
      setIsAuthenticated(true);
    } catch (error: any) {
      setIsRunning(false);

      const status = error?.response?.status;
      
      if (status === 401 ) {
        enqueueSnackbar(t("generic.login.errors.invalid-credentials"), {
          variant: "error",
          autoHideDuration: 10000,
        });
      } else if (status === 403) {
        enqueueSnackbar(t("generic.login.errors.contact-admin"), {
          variant: "error",
          autoHideDuration: 10000,
        });
      } else {
        enqueueSnackbar(t("generic.errors.something-went-wrong"), {
          variant: "error",
          autoHideDuration: 10000,
        });
      }
    }
  };

  React.useEffect(() => {
    if (!isAuthenticated) return;

    const loadPrivateSettings = async () => {
      try {
        const response = await getPrivateSettings();
        dispatch(setPrivateSettings(response));
        navigate(isEntityUser ? "/entities/entity-list" : "/home");
      } catch (error) {
        handleError(error);
      } finally {
        setIsRunning(false);
      }
    };

    loadPrivateSettings();
  }, [isAuthenticated, navigate, dispatch, isEntityUser]);

  return (
    <LoginContainer direction="column" justifyContent="space-between">
      <Box
        sx={{
          position: "fixed",
          top: "1rem",
          right: "1rem",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Fab
          color="primary"
          aria-label={t("generic.cancel")}
          onClick={() => {
            navigate("/");
          }}
        >
          <CancelIcon />
        </Fab>
      </Box>
      <Card variant="outlined">
        <Typography
          component="h1"
          variant="h4"
          sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
        >
          {t("generic.login.title")}
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: 2,
          }}
        >
          <FormControl>
            <FormLabel htmlFor="email">{t("generic.email")}</FormLabel>
            <TextField
              id="email"
              type="email"
              name="email"
              placeholder="your@email.com"
              autoComplete="email"
              autoFocus
              required
              fullWidth
              variant="outlined"
              disabled={isRunning}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="password">{t("generic.password")}</FormLabel>
            <TextField
              name="password"
              placeholder="••••••"
              type="password"
              id="password"
              autoComplete="current-password"
              required
              fullWidth
              variant="outlined"
              disabled={isRunning}
            />
          </FormControl>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            onClick={validateInputs}
            disabled={isRunning}
          >
            {t("generic.login.title")}
          </Button>
          {isRunning && (
            <CircularProgress
              size={24}
              sx={{
                color: "primary.main",
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-12px",
                marginLeft: "-12px",
              }}
            />
          )}
        </Box>
      </Card>
    </LoginContainer>
  );
}
